/**
 * @file inward-grn-approval.tsx
 * @module app/modules/inward/components/inward-grn-approval
 * @description One Inward GRN with its two approvals. Stock goes live only when BOTH are done
 *   (per product: Warehouse has verified the GRN and Finance has approved the product).
 *   - Warehouse: photos taken with the in-app camera (several, no gallery), then approve.
 *   - Finance: documents from the camera or any file upload (any type / size / number), the
 *     invoice number, receipt number and receipt verification code (read automatically from
 *     photos by OCR, or typed), the cost price per product, for one, several or all products.
 *     A cost different from the PO updates the Price Catalog cost and raises a Tier 1 alert
 *     that someone with "Acknowledge Cost Price Change" confirms here.
 *   Everyone sees both approval statuses (who, when, how long pending).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 2.1.2
 */

import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Badge, Button, Card, Form, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import APICallService from "../../../../api/apiCallService";
import { INWARD } from "../../../../api/apiEndPoints";
import Loader from "../../../../Global/loader";
import { error, success } from "../../../../Global/toast";
import { PermissionModule } from "../../../../utils/constants";
import { useWarehouse } from "../../../context/WarehouseContext";
import { useAuth } from "../../auth";
import { canPerformInwardAction, InwardAction } from "../utils/inward-access";
import {
  approvalSuccessMessage,
  buildApprovalForm,
  buildFinanceForm,
  checkApprovalFiles,
  checkFinanceForm,
  FINANCE_FIELDS,
  FinanceForm,
  FinanceLineChoice,
  InwardGrnRecord,
  isApproved,
  isGrnRejected,
  MAX_WAREHOUSE_PHOTOS,
  openFinanceLines,
  needsSellingUpdate,
  pendingCostChanges,
  pendingShortfallCount,
  waitingForStock,
  ReceivedLine,
} from "../utils/inward-approval";
import {
  isReadableImage,
  OcrFieldKey,
  OcrFields,
  readDocuments,
  stopOcr,
} from "../utils/inward-ocr";
import {
  ACTION_LABELS,
  buildTimeline,
  durationBetween,
  formatDateTime,
  inwardStage,
  lineCounts,
  STAGE_LABELS,
} from "../utils/inward-timeline";
import InwardCamera from "./inward-camera";
import InwardImageViewer, {
  fileExtension,
  fileKind,
} from "./inward-image-viewer";
import InwardGrnDecisions from "./inward-grn-decisions";
import InwardReceivedGoods from "./inward-received-goods";
import InwardProcurementPrices from "./inward-procurement-prices";
import "../styles/inward.css";
import ImageWithFallback from "../../_common/components/image-with-fallback";

interface InwardGrnApprovalProps {
  basePath: string;
}

/** Picked files with remove buttons. */
const PickedFiles = ({
  files,
  onRemove,
  testId,
}: {
  files: File[];
  onRemove: (index: number) => void;
  testId: string;
}) =>
  files.length === 0 ? null : (
    <ul className="inward-picked list-unstyled mb-0" data-testid={testId}>
      {files.map((f, i) => (
        <li
          key={`${f.name}-${i}`}
          className="d-flex justify-content-between align-items-center gap-2"
        >
          <span className="text-truncate fs-15">{f.name}</span>
          <Button
            variant="link"
            className="text-danger p-0 fs-15"
            onClick={() => onRemove(i)}
          >
            Remove
          </Button>
        </li>
      ))}
    </ul>
  );

const StatusBadge = ({
  approved,
  label,
  testId,
}: {
  approved: boolean;
  label?: string;
  testId: string;
}) => (
  <Badge
    bg=""
    className={`inward-badge fs-13 py-2 px-3 ${
      approved ? "inward-badge--success" : "inward-badge--warning"
    }`}
    data-testid={testId}
  >
    {label ?? (approved ? "Approved" : "Pending")}
  </Badge>
);

const EMPTY_FIELDS: FinanceForm = {
  invoiceNumber: "",
  receiptNumber: "",
  receiptVerificationCode: "",
};

const InwardGrnApproval = ({ basePath }: InwardGrnApprovalProps) => {
  const navigate = useNavigate();
  const { state } = useLocation() as { state?: { grnId?: string } };
  const grnId = state?.grnId;
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const [grn, setGrn] = useState<InwardGrnRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState<{
    images: string[];
    index: number;
    title?: string;
  } | null>(null);
  const [camera, setCamera] = useState<"" | "warehouse" | "finance">("");

  // Warehouse
  const [photos, setPhotos] = useState<File[]>([]);
  const [savingWarehouse, setSavingWarehouse] = useState(false);
  // Finance: key -> cost price as typed (pre-filled with the PO cost)
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [fields, setFields] = useState<FinanceForm>(EMPTY_FIELDS);
  // fields the user typed in: OCR never overwrites them
  const typed = useRef<Set<OcrFieldKey>>(new Set());
  const [documents, setDocuments] = useState<File[]>([]);
  const [ocrStatus, setOcrStatus] = useState("");
  const [ocrFound, setOcrFound] = useState<OcrFields>({});
  const [savingFinance, setSavingFinance] = useState(false);
  const [busyAlert, setBusyAlert] = useState("");
  const [retrying, setRetrying] = useState(false);
  const mounted = useRef(true);

  const canWarehouse = canPerformInwardAction(
    currentUser,
    InwardAction.WarehouseApprove
  );
  const canFinance = canPerformInwardAction(
    currentUser,
    InwardAction.FinanceApprove
  );
  const canAcknowledge = canPerformInwardAction(
    currentUser,
    InwardAction.AcknowledgeCostPrice
  );
  const canDecide = canPerformInwardAction(
    currentUser,
    InwardAction.DecideShortfall
  );
  const canReject = canPerformInwardAction(currentUser, InwardAction.RejectGrn);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      stopOcr();
    };
  }, []);

  const call = useCallback(
    (endpoint: string, params: unknown, path: unknown = "") =>
      new APICallService(
        endpoint,
        params,
        path,
        "",
        "",
        "",
        PermissionModule.Inward,
        selectedWarehouse?._id
      ).callAPI(),
    [selectedWarehouse?._id]
  );

  // `quiet` refreshes in place after an action (no full-page loader)
  const load = useCallback(
    async (quiet = false) => {
      if (!grnId) return;
      if (!quiet) setLoading(true);
      const response = await call(INWARD.grnInfo, grnId);
      setGrn(response && response._id ? response : null);
      setLoading(false);
    },
    [grnId, call]
  );

  useEffect(() => {
    if (!grnId) {
      navigate(`${basePath}/grns`, { replace: true });
      return;
    }
    load();
  }, [grnId, basePath, navigate, load]);

  const lines = useMemo(() => grn?.receivedLines ?? [], [grn]);
  const linesByKey = useMemo(
    () => new Map(lines.map((l) => [l.key, l])),
    [lines]
  );

  /** Reads Invoice / Receipt / Verification code from new photos; never overwrites typing. */
  const runOcr = async (files: File[]) => {
    const images = files.filter(isReadableImage);
    if (!images.length) return;
    setOcrStatus(
      `Reading ${images.length} document${images.length === 1 ? "" : "s"}…`
    );
    const { fields: found } = await readDocuments(images, (name) => {
      if (mounted.current) setOcrStatus(`Reading ${name}…`);
    });
    if (!mounted.current) return;
    setOcrFound((prev) => ({ ...found, ...prev }));
    setFields((prev) => {
      const next = { ...prev };
      (Object.keys(found) as OcrFieldKey[]).forEach((k) => {
        if (found[k] && !typed.current.has(k) && !prev[k].trim())
          next[k] = found[k] as string;
      });
      return next;
    });
    const count = Object.keys(found).length;
    setOcrStatus(
      count
        ? `Read ${count} of 3 values from the documents. Check them before approving.`
        : "Could not read the values from the documents. Type them in."
    );
  };

  const addDocuments = (files: File[]) => {
    if (!files.length) return;
    setDocuments((prev) => [...prev, ...files]);
    runOcr(files);
  };

  const onFilePick = (e: ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []);
    e.target.value = "";
    addDocuments(list);
  };

  const approveWarehouse = async () => {
    const problem = checkApprovalFiles("warehouse", photos);
    if (problem) {
      error(problem);
      return;
    }
    setSavingWarehouse(true);
    const response = await call(
      INWARD.approveGrn,
      buildApprovalForm("warehouse", photos),
      {
        id: grnId,
        action: "warehouse-approval",
      }
    );
    setSavingWarehouse(false);
    if (!response) return;
    setPhotos([]);
    success(approvalSuccessMessage("warehouse", response));
    await load(true);
  };

  const approveFinance = async () => {
    const choices: FinanceLineChoice[] = Object.entries(picked).map(
      ([key, costPrice]) => {
        const l = linesByKey.get(key) as ReceivedLine;
        return { key, variant: l.variant, type: l.type, costPrice };
      }
    );
    const problem = checkFinanceForm(fields, choices, documents, linesByKey);
    if (problem) {
      error(problem);
      return;
    }
    setSavingFinance(true);
    const response = await call(
      INWARD.approveGrn,
      buildFinanceForm(fields, choices, documents),
      { id: grnId, action: "finance-approval" }
    );
    setSavingFinance(false);
    if (!response) return;
    setPicked({});
    setDocuments([]);
    setFields(EMPTY_FIELDS);
    setOcrFound({});
    setOcrStatus("");
    typed.current = new Set();
    if (response.stockError) {
      error(
        `Finance approval saved, but stock could not be updated: ${response.stockError}. Use "Retry stock update".`
      );
    } else {
      success(approvalSuccessMessage("finance", response));
    }
    await load(true);
  };

  const retryStock = async () => {
    setRetrying(true);
    const response = await call(
      INWARD.retryStock,
      {},
      { id: grnId, action: "retry-stock" }
    );
    setRetrying(false);
    if (response) {
      success("Stock updated");
      await load(true);
    }
  };

  /** After a price step: report stock errors, else confirm. */
  const afterPriceStep = async (response: any, message: string) => {
    if (response.stockError) {
      error(
        `${message}, but stock could not be updated: ${response.stockError}. Use "Retry stock update".`
      );
    } else {
      success(message);
    }
    await load(true);
  };

  const acknowledge = async (
    line: ReceivedLine,
    selling?: { sellingPrice: number; note: string }
  ) => {
    const alertId = line.costChange?.alertId;
    if (!alertId) return;
    setBusyAlert(line.key);
    const response = await call(INWARD.acknowledgeCost, selling ?? {}, {
      id: grnId,
      a: "cost-changes",
      alertId: encodeURIComponent(alertId),
      b: "acknowledge",
    });
    setBusyAlert("");
    if (response) {
      await afterPriceStep(
        response,
        selling
          ? `Cost price of ${line.title} confirmed and selling price updated to ${selling.sellingPrice}`
          : `Cost price of ${line.title} confirmed; the PO is updated`
      );
    }
  };

  const updateSelling = async (
    line: ReceivedLine,
    selling: { sellingPrice: number; note: string }
  ) => {
    setBusyAlert(line.key);
    const response = await call(
      INWARD.updateSellingPrice,
      { variant: line.variant, type: line.type, ...selling },
      { id: grnId, a: "selling-price" }
    );
    setBusyAlert("");
    if (response) {
      await afterPriceStep(
        response,
        `Selling price of ${line.title} updated to ${selling.sellingPrice} in the Price Catalog`
      );
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-10">
        <Loader loading={loading} />
      </div>
    );
  }
  if (!grn) {
    return (
      <div className="inward-empty" data-testid="grn-not-found">
        GRN not found.
      </div>
    );
  }

  const warehouse = grn.inward?.warehouseApproval;
  const warehouseDone = isApproved(warehouse);
  const open = openFinanceLines(grn);
  const counts = lineCounts(grn);
  const financeDone = counts.total > 0 && open.length === 0;
  const costs = pendingCostChanges(grn);
  const stage = STAGE_LABELS[inwardStage(grn)];
  // Held-back products (cost above selling price) need procurement, not a stock retry
  const waitingCredit = warehouseDone && waitingForStock(grn).length > 0;
  const sellingUpdates = lines.filter(needsSellingUpdate);
  const submissions = grn.inward?.financeSubmissions ?? [];
  const lastSubmission = submissions[submissions.length - 1];
  const doneAt = grn.inward?.doneAt ?? grn._createdAt ?? grn.date;
  const rejected = isGrnRejected(grn);
  // the GRN is approved only after every missing / damaged quantity is decided
  const shortfallsOpen = pendingShortfallCount(grn);

  return (
    <div className="inward-page" data-testid="grn-approval">
      <div className="inward-header d-flex justify-content-between align-items-start gap-3 flex-wrap">
        <div>
          <h1 className="fs-22 fw-bolder mb-1">GRN-{grn.refKey}</h1>
          <p className="text-muted fs-15 mb-0">
            PO {grn.poRefKey || "-"} ·{" "}
            {grn.businessDetails?.name || grn.businessDetails?.ownerName || "-"}
            {grn.warehouse?.name ? ` · ${grn.warehouse.name}` : ""}
          </p>
          <p className="fs-15 mb-0" data-testid="grn-header-assignment">
            Loading bay manager:{" "}
            <strong>{grn.inward?.loadingBayManager?.name || "-"}</strong> ·
            Dock: <strong>{grn.inward?.dock?.name || "-"}</strong> · Gate entry:{" "}
            <strong>{grn.gateEntryNumber || "-"}</strong>
          </p>
          <p className="fs-14 text-muted mb-0" data-testid="grn-header-times">
            Inward started {formatDateTime(grn.inward?.startedAt)} · Inward done{" "}
            {formatDateTime(doneAt)}
            {grn.inward?.startedAt
              ? ` (took ${durationBetween(grn.inward.startedAt, doneAt)})`
              : ""}
          </p>
          <p className="fs-14 text-muted mb-0">
            Driver {grn.driverName || "-"} ({grn.driverPhone || "-"}) · Vehicle{" "}
            {grn.vehicleNumber || "-"}
            {grn.receiptDate
              ? ` · Delivered ${formatDateTime(grn.receiptDate)}`
              : ""}
          </p>
        </div>
        <Button
          variant="outline-primary"
          className="inward-touch-btn"
          onClick={() => navigate(`${basePath}/grns`)}
        >
          Back
        </Button>
      </div>

      <InwardGrnDecisions
        grn={grn}
        canDecide={canDecide}
        canReject={canReject}
        call={call}
        onChanged={() => load(true)}
      />

      {/* Both roles see where the other approval stands */}
      <Card className="inward-card mb-5" data-testid="grn-approval-status">
        <Card.Body className="inward-status-grid">
          <div data-testid="status-warehouse">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="fs-15 fw-bold">Warehouse manager</span>
              <StatusBadge
                approved={warehouseDone}
                label={rejected && !warehouseDone ? "Rejected" : undefined}
                testId="status-warehouse-badge"
              />
            </div>
            <div className="fs-14">
              {warehouseDone
                ? `Approved by ${warehouse?.byName || "-"} on ${formatDateTime(
                    warehouse?.at
                  )}`
                : `Pending for ${durationBetween(
                    doneAt,
                    null
                  )} (since ${formatDateTime(doneAt)})`}
            </div>
          </div>
          <div data-testid="status-finance">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="fs-15 fw-bold">Finance manager</span>
              <StatusBadge
                approved={financeDone}
                label={
                  financeDone
                    ? "Approved"
                    : `${counts.approved} of ${counts.total} approved`
                }
                testId="status-finance-badge"
              />
            </div>
            <div className="fs-14">
              {lastSubmission
                ? `Last approved by ${
                    lastSubmission.by?.name || "-"
                  } on ${formatDateTime(lastSubmission.at)}`
                : ""}
              {!financeDone &&
                `${lastSubmission ? " · " : ""}${open.length} product${
                  open.length === 1 ? "" : "s"
                } pending for ${durationBetween(doneAt, null)}`}
            </div>
          </div>
          <div className="fs-14 text-muted inward-status-note">
            Stock goes live only when the Warehouse manager and the Finance
            manager have both approved.
          </div>
        </Card.Body>
      </Card>

      <Card className="inward-card mb-5">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h2 className="fs-18 fw-bold mb-0">Received goods</h2>
            <Badge
              bg=""
              className={`inward-badge inward-badge--${stage.variant} fs-14 py-2 px-3`}
              data-testid="grn-stage"
            >
              {stage.label}
            </Badge>
          </div>
          <InwardReceivedGoods
            lines={lines}
            totals={grn.totals}
            selectable={canFinance && open.length > 0}
            selected={picked}
            onToggle={(l, checked) =>
              setPicked((prev) => {
                const next = { ...prev };
                if (checked) next[l.key] = String(l.poCostPrice);
                else delete next[l.key];
                return next;
              })
            }
            onToggleAll={(checked) =>
              setPicked((prev) =>
                checked
                  ? Object.fromEntries(
                      open.map((l) => [
                        l.key,
                        prev[l.key] ?? String(l.poCostPrice),
                      ])
                    )
                  : {}
              )
            }
            // Typing a cost selects the product
            onCostChange={(l, value) =>
              setPicked((prev) => ({ ...prev, [l.key]: value }))
            }
          />
          {waitingCredit && canFinance && (
            <div className="alert alert-warning mt-3 mb-0 d-flex justify-content-between align-items-center gap-3 flex-wrap">
              <span>Some approved products are not live in stock yet.</span>
              <Button
                variant="warning"
                className="inward-touch-btn"
                disabled={retrying}
                onClick={retryStock}
                data-testid="grn-retry-stock"
              >
                {retrying ? "Updating…" : "Retry stock update"}
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      <InwardProcurementPrices
        costChanges={costs}
        sellingUpdates={sellingUpdates}
        canAct={canAcknowledge}
        busyKey={busyAlert}
        onAcknowledge={acknowledge}
        onUpdateSelling={updateSelling}
        onError={error}
      />

      {!rejected && shortfallsOpen > 0 && (
        <div
          className="alert alert-warning mb-3"
          data-testid="grn-approve-blocked"
        >
          Approve or reject the missing / damaged quantities above before
          approving this GRN.
        </div>
      )}
      {/* A rejected GRN takes no more approvals */}
      <div
        className={`inward-approval-grid mb-5${rejected ? " d-none" : ""}`}
        aria-hidden={rejected}
        data-testid="grn-approval-panels"
      >
        <Card className="inward-card h-100" data-testid="approval-warehouse">
          <Card.Body className="d-flex flex-column gap-3">
            <div className="d-flex justify-content-between align-items-center gap-2">
              <h2 className="fs-18 fw-bold mb-0">Warehouse approval</h2>
              <StatusBadge
                approved={warehouseDone}
                testId="approval-warehouse-status"
              />
            </div>
            {warehouseDone ? (
              <>
                <div className="fs-14 text-muted">
                  {`Approved on ${formatDateTime(warehouse?.at)}`}
                  {warehouse?.byName ? ` by ${warehouse.byName}` : ""}
                </div>
                <div className="inward-file-list">
                  {(warehouse?.images ?? []).map((url, i, all) => (
                    <button
                      key={url}
                      type="button"
                      className="inward-photo-btn"
                      onClick={() => setViewer({ images: all, index: i })}
                      data-testid="approval-photo"
                    >
                      <ImageWithFallback
                        src={url}
                        alt={`Verification ${i + 1}`}
                        className="inward-photo"
                        noDefaultSize
                      />
                    </button>
                  ))}
                </div>
              </>
            ) : !canWarehouse ? (
              <div
                className="fs-15 text-muted"
                data-testid="approval-warehouse-no-access"
              >
                Waiting for a warehouse approver.
              </div>
            ) : (
              <>
                <p className="fs-15 mb-0">
                  Check the goods against this GRN, take photos with the camera
                  as proof (up to {MAX_WAREHOUSE_PHOTOS}), then approve.
                </p>
                <Button
                  className="inward-touch-btn"
                  onClick={() => setCamera("warehouse")}
                  data-testid="approval-warehouse-camera"
                >
                  {photos.length ? "Take more photos" : "Open camera"}
                </Button>
                <PickedFiles
                  files={photos}
                  onRemove={(i) =>
                    setPhotos((prev) => prev.filter((_, x) => x !== i))
                  }
                  testId="approval-warehouse-files"
                />
                <Button
                  variant="success"
                  className="inward-touch-btn mt-auto"
                  disabled={
                    savingWarehouse || photos.length === 0 || shortfallsOpen > 0
                  }
                  onClick={approveWarehouse}
                  data-testid="approval-warehouse-submit"
                >
                  {savingWarehouse
                    ? "Approving…"
                    : `Approve (verified, ${photos.length} photo${
                        photos.length === 1 ? "" : "s"
                      })`}
                </Button>
              </>
            )}
          </Card.Body>
        </Card>

        <Card className="inward-card h-100" data-testid="approval-finance">
          <Card.Body className="d-flex flex-column gap-3">
            <div className="d-flex justify-content-between align-items-center gap-2">
              <h2 className="fs-18 fw-bold mb-0">Finance approval</h2>
              <StatusBadge
                approved={financeDone}
                label={
                  financeDone
                    ? "Approved"
                    : `${counts.approved} of ${counts.total} approved`
                }
                testId="approval-finance-status"
              />
            </div>

            {submissions.map((sub, i) => (
              <div
                key={i}
                className="inward-submission fs-14"
                data-testid="finance-submission"
              >
                <div className="fw-semibold">
                  {sub.lines?.length ?? 0} product(s) · {formatDateTime(sub.at)}
                  {sub.by?.name ? ` · ${sub.by.name}` : ""}
                </div>
                <div className="text-muted">
                  Invoice {sub.invoiceNumber} · Receipt {sub.receiptNumber} ·
                  Verification {sub.receiptVerificationCode}
                </div>
                <div className="d-flex gap-2 flex-wrap mt-2">
                  {(sub.documents ?? []).map((url, d, all) => (
                    <button
                      key={url}
                      type="button"
                      className="inward-doc-btn"
                      onClick={() =>
                        setViewer({
                          images: all,
                          index: d,
                          title: "Finance document",
                        })
                      }
                      data-testid="finance-document"
                    >
                      {fileKind(url) === "image" ? (
                        <ImageWithFallback src={url} alt={`Document ${d + 1}`} noDefaultSize />
                      ) : (
                        <span className="inward-viewer-file-ext fs-12 px-2 py-1">
                          {fileExtension(url) || "FILE"}
                        </span>
                      )}
                      <span className="fs-14">Document {d + 1}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {open.length === 0 ? null : !canFinance ? (
              <div
                className="fs-15 text-muted"
                data-testid="approval-finance-no-access"
              >
                Waiting for a finance approver ({open.length} products).
              </div>
            ) : (
              <>
                <p className="fs-15 mb-0">
                  1. Add the documents (photo or any file). Invoice, receipt and
                  verification code are read from photos automatically; you can
                  type them at any time.
                  <br />
                  2. Tick the products in the table (one or all) and check the
                  cost price.
                </p>
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    variant="outline-primary"
                    className="inward-touch-btn flex-fill"
                    onClick={() => setCamera("finance")}
                    data-testid="approval-finance-camera"
                  >
                    Take photo
                  </Button>
                  <Form.Label className="btn btn-outline-primary inward-touch-btn flex-fill d-flex align-items-center justify-content-center mb-0">
                    Upload files
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={onFilePick}
                      data-testid="approval-finance-input"
                    />
                  </Form.Label>
                </div>
                <PickedFiles
                  files={documents}
                  onRemove={(i) =>
                    setDocuments((prev) => prev.filter((_, x) => x !== i))
                  }
                  testId="approval-finance-files"
                />
                {ocrStatus && (
                  <div
                    className="alert alert-info mb-0 fs-14 py-2"
                    data-testid="finance-ocr-status"
                  >
                    {ocrStatus}
                  </div>
                )}
                {FINANCE_FIELDS.map((f) => (
                  <Form.Group key={f.key} controlId={`finance-${f.key}`}>
                    <Form.Label className="fs-15 fw-semibold">
                      {f.label}
                    </Form.Label>
                    <Form.Control
                      className="inward-input"
                      value={fields[f.key]}
                      onChange={(e) => {
                        typed.current.add(f.key);
                        setFields((prev) => ({
                          ...prev,
                          [f.key]: e.target.value,
                        }));
                      }}
                      data-testid={`finance-${f.key}`}
                    />
                    {ocrFound[f.key] && ocrFound[f.key] !== fields[f.key] && (
                      <Button
                        variant="link"
                        className="p-0 fs-13"
                        onClick={() =>
                          setFields((prev) => ({
                            ...prev,
                            [f.key]: ocrFound[f.key] as string,
                          }))
                        }
                        data-testid={`finance-ocr-use-${f.key}`}
                      >
                        Use read value: {ocrFound[f.key]}
                      </Button>
                    )}
                  </Form.Group>
                ))}
                <div
                  className="fs-15 fw-semibold"
                  data-testid="finance-selected"
                >
                  Selected: {Object.keys(picked).length} of {open.length}{" "}
                  remaining
                </div>
                <Button
                  variant="success"
                  className="inward-touch-btn mt-auto"
                  disabled={
                    savingFinance ||
                    Object.keys(picked).length === 0 ||
                    shortfallsOpen > 0
                  }
                  onClick={approveFinance}
                  data-testid="approval-finance-submit"
                >
                  {savingFinance
                    ? "Approving…"
                    : warehouseDone
                    ? `Approve ${Object.keys(picked).length} & make stock live`
                    : `Approve ${
                        Object.keys(picked).length
                      } (live after Warehouse)`}
                </Button>
              </>
            )}
          </Card.Body>
        </Card>
      </div>

      <Card className="inward-card mb-5" data-testid="grn-timeline">
        <Card.Body>
          <h2 className="fs-18 fw-bold mb-3">Timeline</h2>
          <ol className="inward-timeline">
            {buildTimeline(grn).map((step) => (
              <li
                key={step.key}
                className={step.done ? "done" : "waiting"}
                data-testid={`timeline-${step.key}`}
              >
                <span className="inward-timeline-dot" />
                <div>
                  <div className="fs-15 fw-semibold">{step.label}</div>
                  <div className="fs-14 text-muted">
                    {step.done
                      ? `${formatDateTime(step.at)}${
                          step.by ? ` · ${step.by}` : ""
                        }`
                      : step.waitingLabel || "Not recorded"}
                  </div>
                </div>
                <div className="fs-14 text-end">
                  {step.key === "started"
                    ? ""
                    : step.done
                    ? `took ${step.duration}`
                    : step.duration === "-"
                    ? ""
                    : `waiting ${step.duration}`}
                </div>
              </li>
            ))}
          </ol>

          {(grn.inward?.actions ?? []).length > 0 && (
            <>
              <h3 className="fs-16 fw-bold mt-4 mb-2">Who did what</h3>
              <Table size="sm" className="fs-14 mb-0" data-testid="grn-actions">
                <tbody>
                  {(grn.inward?.actions ?? []).map((a, i) => (
                    <tr key={`${a.action}-${i}`}>
                      <td>{ACTION_LABELS[a.action] ?? a.action}</td>
                      <td>{a.by?.name || "-"}</td>
                      <td>{formatDateTime(a.at)}</td>
                      <td className="text-muted">{a.note || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Card.Body>
      </Card>

      <InwardCamera
        show={camera !== ""}
        title={
          camera === "warehouse"
            ? "Verification photos"
            : "Photo of the document"
        }
        prefix={camera === "warehouse" ? "warehouse" : "finance"}
        onCapture={(file) => {
          if (camera === "warehouse") setPhotos((prev) => [...prev, file]);
          else addDocuments([file]);
        }}
        onClose={() => setCamera("")}
      />

      <InwardImageViewer
        images={viewer?.images ?? []}
        openIndex={viewer ? viewer.index : null}
        onClose={() => setViewer(null)}
        title={viewer?.title ?? "Verification photo"}
      />
    </div>
  );
};

export default InwardGrnApproval;
